import { MoreVert as MoreVertIcon, Search as SearchIcon } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Customer, Examination, Lead, Project, Status } from "graphql/types"
import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { groupDevicesIntoTestSetups, TestSetup } from "utils/testSetupGrouping"
import { getBusinessCustomerName } from "./ProjectViewsUtils"

type MobileExaminationViewProps = {
  projects: Project[]
  selectedEmployeeId: string
  leads: Lead[]
  customers: Customer[]
}

// Hilfsfunktion um den Status-Text zu erhalten
const getStatusText = (status: Status): string => {
  switch (status) {
    case Status.OPEN:
      return "Offen"
    case Status.IN_PROGRESS:
      return "In Bearbeitung"
    case Status.DONE:
      return "Abgeschlossen"
    case Status.PAUSED:
      return "Pausiert"
    case Status.BILLED:
      return "Abgerechnet"
    default:
      return "Unbekannt"
  }
}

// Hilfsfunktion um die Status-Farbe zu erhalten
const getStatusColor = (
  status: Status
): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case Status.OPEN:
      return "default"
    case Status.IN_PROGRESS:
      return "primary"
    case Status.DONE:
      return "success"
    case Status.PAUSED:
      return "warning"
    case Status.BILLED:
      return "info"
    default:
      return "default"
  }
}

// Hilfsfunktion um den Fortschritt zu berechnen
const calculateProgress = (examination: Examination): number => {
  if (!examination.tasks || examination.tasks.length === 0) {
    return 0
  }

  const completedTasks = examination.tasks.filter((task) => task.status === Status.DONE).length
  return Math.round((completedTasks / examination.tasks.length) * 100)
}

// Hilfsfunktion um die Geräteliste kompakt zu formatieren
const formatDeviceList = (deviceNames: string[]): string => {
  if (!deviceNames || deviceNames.length === 0) {
    return ''
  }
  // Now the format is already simplified: "1x Device-123: DeviceName"
  return deviceNames.join(', ')
}

type allTestSetupsType = {
  id: string
  project: Project
  testSetup: TestSetup
  examination: Examination
  companyName: string
  examinationName: string
  status: Status
  progress: number
  deviceNames: string[]
}

export const MobileExaminationView = ({
  projects,
  selectedEmployeeId,
  leads,
  customers,
}: MobileExaminationViewProps) => {
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [searchText, setSearchText] = useState<string>("")

  // Alle TestSetups aus allen Projekten extrahieren
  const allTestSetups = useMemo(() => {
    const setups: Array<allTestSetupsType> = []

    projects.forEach((project) => {
      const { groupedDevices } = groupDevicesIntoTestSetups(project)
      const companyName = getBusinessCustomerName(
        project.lead.opportunityID || "",
        leads,
        customers
      )

      groupedDevices.forEach((group) => {
        group.testSetups.forEach((testSetup, testSetupIndex) => {
          // Jede Examination im TestSetup wird als separate Zeile angezeigt
          testSetup.examinations.forEach((examination) => {
            const deviceNames = testSetup.devices.map(
              (device) => {
                const deviceCount = device.devices?.length || 1
                return `${deviceCount}x ${device.barcode}: ${device.name}`
              }
            )

            setups.push({
              id: `${project.id}-${examination.id}-${testSetupIndex}`,
              project,
              testSetup,
              examination,
              companyName,
              examinationName: examination.name,
              status: examination.status,
              progress: calculateProgress(examination),
              deviceNames,
            })
          })
        })
      })
    })

    // TODO: TestSetups filtern nach selectedEmployeeId

    return setups
  }, [projects, leads, customers])

  // Gefilterte TestSetups basierend auf Status und Suchtext
  const filteredTestSetups = useMemo(() => {
    return allTestSetups.filter((testSetup) => {
      // Status-Filter
      if (statusFilter !== "all" && testSetup.status !== statusFilter) {
        return false
      }

      // Suchtext-Filter
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase()
        const matchesCompanyName = testSetup.companyName.toLowerCase().includes(searchLower)
        const matchesExaminationName = testSetup.examinationName.toLowerCase().includes(searchLower)
        const matchesProjectName = testSetup.project.name.toLowerCase().includes(searchLower)

        if (!matchesCompanyName && !matchesExaminationName && !matchesProjectName) {
          return false
        }
      }

      return true
    })
  }, [allTestSetups, statusFilter, searchText])

  // Handle examination card click
  const handleExaminationClick = useCallback(
    (examinationId: string, projectId: string) => {
      navigate(`/examination/${projectId}/${examinationId}`)
    },
    [navigate]
  )

  // Handle more options click
  const handleMoreOptionsClick = useCallback((event: React.MouseEvent, examinationId: string) => {
    event.stopPropagation()
    // TODO: Implementierung für weitere Optionen
    console.log("Weitere Optionen:", examinationId)
  }, [])

  // Handle status filter change
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value)
  }

  // Handle search text change
  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  return (
    <Box sx={{ p: 2, pb: 8 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "medium" }}>
        TestSetups ({filteredTestSetups.length})
      </Typography>

      {/* Filter-Bereich */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexDirection: { xs: "column", sm: "row" } }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={handleStatusFilterChange}>
            <MenuItem value="all">Alle</MenuItem>
            <MenuItem value={Status.OPEN}>Offen</MenuItem>
            <MenuItem value={Status.IN_PROGRESS}>In Bearbeitung</MenuItem>
            <MenuItem value={Status.DONE}>Abgeschlossen</MenuItem>
            <MenuItem value={Status.PAUSED}>Pausiert</MenuItem>
            <MenuItem value={Status.BILLED}>Abgerechnet</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Suche nach Firma, Prüfung..."
          value={searchText}
          onChange={handleSearchTextChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {filteredTestSetups.map((testSetup) => (
          <Card
            key={testSetup.id}
            sx={{
              cursor: "pointer",
              "&:hover": {
                boxShadow: 3,
                transform: "translateY(-1px)",
                transition: "all 0.2s ease-in-out",
              },
            }}
            onClick={() => handleExaminationClick(testSetup.examination.id, testSetup.project.id)}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Header mit Firma, Projekt, Status und Fortschritt */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight="medium" color="primary">
                    {testSetup.companyName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testSetup.project.name}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    label={getStatusText(testSetup.status)}
                    color={getStatusColor(testSetup.status)}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: "2rem" }}>
                    {testSetup.progress}%
                  </Typography>
                  <Tooltip title="Weitere Optionen">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMoreOptionsClick(e, testSetup.examination.id)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* TestSetup Name und Norm */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight="medium" sx={{ mb: 0.5 }}>
                  {testSetup.examinationName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {testSetup.examination.norm?.name || "Keine Norm"}
                </Typography>
                {testSetup.deviceNames.length > 0 && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 0.5, 
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    Geräte: {formatDeviceList(testSetup.deviceNames)}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {filteredTestSetups.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            color: "text.secondary",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Keine TestSetups gefunden
          </Typography>
          <Typography variant="body2">Es sind derzeit keine TestSetups verfügbar.</Typography>
        </Box>
      )}
    </Box>
  )
}
